import React from 'react';
import { observer } from 'mobx-react-lite';

import Loader from 'src/components/common/loader';
import Button from 'src/components/ui/button';
import Input from 'src/components/ui/input';
import InputPhone from 'src/components/ui/input-phone';

import popupStore from 'src/stores/popup-store';

import useContactRequest from 'src/hooks/use-contact-request';
import { CountryCode, InputMaxLength, LOCALES, PopupName, Regex } from 'src/constants';
import type { ICommonPopupProps } from 'src/interfaces';

const PopupContact = observer(({ handleCloseButtonClick }: ICommonPopupProps) => {
  const { deceasedId } = popupStore.getPopupState(PopupName.CONTACT);

  const handleSubmit = () => {
    popupStore.showPopup(PopupName.INFO, {
      title: LOCALES.THANK_YOU,
      text: LOCALES.WE_WILL_CONTACT_YOU_SHORTLY,
    });
  };

  const handleError = (errorText?: string) => {
    const text = Regex.PHONE_INVALID.test(errorText ?? '')
      ? LOCALES.PHONE_FORMAT
      : errorText || LOCALES.PLEASE_TRY_AGAIN_LATER;

    popupStore.showPopup(PopupName.INFO, {
      title: LOCALES.SOMETHING_WENT_WRONG,
      text,
    });
  };

  const { handleChange, handleSubmitButtonClick, formObject, isSending } = useContactRequest(
    handleSubmit,
    handleError,
    deceasedId
  );

  const handleChangeCountryCode = React.useMemo(() => handleChange('countryCode'), [handleChange]);
  const handleChangePhone = React.useMemo(() => handleChange('phone'), [handleChange]);

  const { countryCode, email, message, name, phone } = formObject;

  return (
    <>
      <div className="popup__header">
        <p className="popup__title">{LOCALES.CONTACT_CEMETERY}</p>
        <p className="popup__text">{LOCALES.ASK_YOUR_QUESTION}</p>
      </div>
      <form className="popup__form" autoComplete="off">
        <Input
          id="name"
          label={LOCALES.YOUR_NAME}
          isDisabled={isSending}
          isValid={name.isValid}
          maxLength={InputMaxLength.TEXT}
          onChange={handleChange('name')}
          textError={name.textError}
          value={name.value}
        />

        <InputPhone
          countryCode={countryCode.value as CountryCode}
          id="phone"
          isDisabled={isSending}
          isValid={phone.isValid}
          label={LOCALES.PHONE_NUMBER}
          onChange={handleChangePhone}
          onChangeCountryCode={handleChangeCountryCode}
          textError={phone.textError}
          value={phone.value}
        />

        <Input
          id="email"
          isDisabled={isSending}
          isPlaceholderOnFocus
          isValid={email.isValid}
          label={LOCALES.EMAIL}
          maxLength={InputMaxLength.EMAIL}
          onChange={handleChange('email')}
          placeholder="user@example.com"
          textError={email.textError}
          value={email.value}
        />

        <Input
          hasCounter
          id="message"
          isDisabled={isSending}
          isValid={message.isValid}
          label={LOCALES.YOUR_MESSAGE}
          maxLength={InputMaxLength.TEXT}
          onChange={handleChange('message')}
          rows={3}
          textError={message.textError}
          value={message.value}
        />
      </form>
      <div className="popup__buttons">
        <Button
          className="popup__button"
          isDisabled={isSending}
          label={LOCALES.CANCEL}
          onClick={handleCloseButtonClick}
          size="oversize"
          tag="button"
          theme="outlined"
        />
        <Button
          className="popup__button"
          isDisabled={isSending}
          label={LOCALES.SUBMIT}
          onClick={handleSubmitButtonClick}
          size="oversize"
          tag="button"
          theme="filled"
        />
      </div>

      {isSending && <Loader className="popup__loader" />}
    </>
  );
});

export default PopupContact;
